"use client";

import { useQueries, useQueryClient } from "@tanstack/react-query";
import { useMemo } from "react";
import type { BuddyRelationship } from "@/lib/buddy/relationship";
import { buddyRelationshipOptions } from "@/lib/buddy/relationship-query";

/** Every list and Connect button observes the same per-person cache entry.
 * Reads for all visible people are batched into one server action.
 * Unknown relationships remain placeholders until the lookup succeeds.
 */
export function useBuddyRelationships(userIds: string[]): {
  relationshipOf: (userId: string) => BuddyRelationship | undefined;
  isLoading: boolean;
} {
  const client = useQueryClient();
  const sortedUserIds = useMemo(
    () => [...new Set(userIds.filter(Boolean))].sort(),
    [userIds],
  );
  const queries = useQueries({
    queries: sortedUserIds.map((userId) =>
      buddyRelationshipOptions(client, userId),
    ),
  });
  const relationships = new Map(
    sortedUserIds.map((id, index) => [id, queries[index].data]),
  );
  return {
    relationshipOf: (id) => relationships.get(id),
    isLoading: queries.some((query) => query.isLoading),
  };
}
